import React, { useState, useCallback, useMemo } from 'react';
import {
  ClipboardDocumentIcon, CheckIcon,
  ChevronDownIcon, ChevronRightIcon,
  MagnifyingGlassIcon, XMarkIcon,
} from '@heroicons/react/20/solid';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function CopyValue({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <button
      onClick={copy}
      title="Copy value"
      className="ml-1.5 align-middle inline-flex items-center text-gray-300 dark:text-gray-600 hover:text-indigo-500 dark:hover:text-indigo-400 transition-colors"
    >
      {copied
        ? <CheckIcon className="w-3 h-3 text-green-500" />
        : <ClipboardDocumentIcon className="w-3 h-3" />}
    </button>
  );
}

/* ── Returns true if value or any descendant contains q ── */
function subtreeHasMatch(value, q) {
  if (value === null) return 'null'.includes(q);
  if (typeof value !== 'object') return String(value).toLowerCase().includes(q);
  const entries = Array.isArray(value)
    ? value.map((v, i) => [i, v])
    : Object.entries(value);
  return entries.some(([k, v]) =>
    String(k).toLowerCase().includes(q) || subtreeHasMatch(v, q)
  );
}

/* ── Count nodes where key or primitive value matches ── */
function countMatches(value, q) {
  if (!q) return 0;
  let n = 0;
  if (value === null || typeof value !== 'object') return 0;
  const entries = Array.isArray(value)
    ? value.map((v, i) => [i, v])
    : Object.entries(value);
  for (const [k, v] of entries) {
    const keyHit = String(k).toLowerCase().includes(q);
    const valHit = (v === null || typeof v !== 'object') &&
      String(v === null ? 'null' : v).toLowerCase().includes(q);
    if (keyHit || valHit) n++;
    if (v !== null && typeof v === 'object') n += countMatches(v, q);
  }
  return n;
}

/* ── Inline text highlighter ── */
function Highlight({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const parts = [];
  const lower = text.toLowerCase();
  let i = 0;
  while (i < text.length) {
    const idx = lower.indexOf(query, i);
    if (idx === -1) { parts.push(text.slice(i)); break; }
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(
      <mark key={idx} className="bg-yellow-300 dark:bg-yellow-500 text-gray-900 rounded-sm px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
    );
    i = idx + query.length;
  }
  return <>{parts}</>;
}

/* ── Coloured primitive value ── */
function ValueToken({ value, query }) {
  if (value === null)
    return <span className="text-gray-400 dark:text-gray-500"><Highlight text="null" query={query} /></span>;
  if (typeof value === 'boolean')
    return <span className="text-amber-600 dark:text-amber-400"><Highlight text={String(value)} query={query} /></span>;
  if (typeof value === 'number')
    return <span className="text-blue-600 dark:text-blue-400"><Highlight text={String(value)} query={query} /></span>;
  if (typeof value === 'string') {
    const isId = UUID_RE.test(value);
    return (
      <span className="inline-flex items-center">
        <span className="text-green-700 dark:text-green-400">"<Highlight text={value} query={query} />"</span>
        {isId && <CopyValue value={value} />}
      </span>
    );
  }
  return <span className="text-gray-700 dark:text-gray-300"><Highlight text={String(value)} query={query} /></span>;
}

/* ── Recursive tree node ── */
function JsonNode({ nodeKey, value, depth = 0, defaultOpen = true, query = '' }) {
  const [open, setOpen] = useState(depth < 2 ? defaultOpen : false);
  const indent = depth * 16;

  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);
  const entries = isObject
    ? (isArray ? value.map((v, i) => [i, v]) : Object.entries(value).sort(([a], [b]) => a.localeCompare(b)))
    : null;
  const count = entries?.length ?? 0;
  const bracket = isArray ? ['[', ']'] : ['{', '}'];

  const q = query.toLowerCase();

  const keyStr   = nodeKey !== undefined ? String(nodeKey) : '';
  const keyHit   = !!(q && keyStr.toLowerCase().includes(q));
  const valueStr = !isObject ? (value === null ? 'null' : String(value)) : '';
  const valueHit = !!(q && !isObject && valueStr.toLowerCase().includes(q));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const descendantHit = useMemo(() => isObject && q ? subtreeHasMatch(value, q) : false, [q]);

  // Hide node entirely when query is active and nothing matches anywhere
  if (q && !keyHit && !valueHit && !descendantHit) return null;

  // Auto-open when a descendant matches; manual toggle still works when no query
  const isOpen = (q && descendantHit) ? true : open;

  const keyDisplay = nodeKey !== undefined
    ? (typeof nodeKey === 'number' ? String(nodeKey) : `"${nodeKey}"`)
    : null;

  const rowHighlight = (keyHit || valueHit)
    ? 'bg-yellow-50 dark:bg-yellow-900/20'
    : '';

  return (
    <div style={{ marginLeft: depth > 0 ? indent : 0 }}>
      <div className={`flex items-start gap-1 leading-6 rounded ${rowHighlight}`}>
        {/* expand/collapse toggle */}
        {isObject ? (
          <button
            onClick={() => setOpen(o => !o)}
            className="mt-0.5 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            {isOpen
              ? <ChevronDownIcon className="w-3.5 h-3.5" />
              : <ChevronRightIcon className="w-3.5 h-3.5" />}
          </button>
        ) : (
          <span className="w-3.5 flex-shrink-0" />
        )}

        {/* key */}
        {keyDisplay !== null && (
          <span className="text-rose-600 dark:text-rose-400 flex-shrink-0">
            <Highlight text={keyDisplay} query={q} />
            <span className="text-gray-500 dark:text-gray-400">: </span>
          </span>
        )}

        {/* primitive value */}
        {!isObject && <ValueToken value={value} query={q} />}

        {/* collapsed summary */}
        {isObject && !isOpen && (
          <button
            onClick={() => setOpen(true)}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors text-xs"
          >
            {bracket[0]}
            <span className="mx-1 italic">{count} {count === 1 ? 'item' : 'items'}</span>
            {bracket[1]}
          </button>
        )}

        {/* open bracket */}
        {isObject && isOpen && (
          <span className="text-gray-500 dark:text-gray-400">{bracket[0]}</span>
        )}
      </div>

      {/* children */}
      {isObject && isOpen && (
        <>
          <div>
            {entries.map(([k, v]) => (
              <JsonNode
                key={k}
                nodeKey={k}
                value={v}
                depth={depth + 1}
                defaultOpen={defaultOpen}
                query={query}
              />
            ))}
          </div>
          <div style={{ marginLeft: indent }} className="flex items-center gap-1 leading-6">
            <span className="w-3.5 flex-shrink-0" />
            <span className="text-gray-500 dark:text-gray-400">{bracket[1]}</span>
          </div>
        </>
      )}
    </div>
  );
}

/* ── Main component ── */
const CodeSnippetView = ({ data }) => {
  const [copied, setCopied]   = useState(false);
  const [allOpen, setAllOpen] = useState(true);
  const [treeKey, setTreeKey] = useState(0);
  const [query, setQuery]     = useState('');

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [data]);

  const toggleAll = useCallback((open) => {
    setAllOpen(open);
    setTreeKey(k => k + 1);
  }, []);

  const matchCount = useMemo(
    () => countMatches(data, query.toLowerCase()),
    [data, query]
  );

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">

        {/* expand / collapse */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <button onClick={() => toggleAll(true)}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
            Expand all
          </button>
          <span className="text-gray-300 dark:text-gray-600">|</span>
          <button onClick={() => toggleAll(false)}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">
            Collapse all
          </button>
        </div>

        {/* search box */}
        <div className="flex items-center gap-1.5 flex-1 min-w-[160px] max-w-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1">
          <MagnifyingGlassIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search keys / values…"
            className="flex-1 text-xs bg-transparent text-gray-700 dark:text-gray-300 placeholder-gray-400 outline-none"
          />
          {query && (
            <>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {matchCount} {matchCount === 1 ? 'match' : 'matches'}
              </span>
              <button onClick={() => setQuery('')} className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>

        {/* copy */}
        <button onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors flex-shrink-0 ml-auto">
          {copied
            ? <><CheckIcon className="w-3.5 h-3.5 text-green-500" /> Copied</>
            : <><ClipboardDocumentIcon className="w-3.5 h-3.5" /> Copy JSON</>}
        </button>
      </div>

      {/* tree */}
      <div className="p-4 bg-gray-50 dark:bg-gray-900 overflow-x-auto text-sm font-mono">
        {query && matchCount === 0 ? (
          <p className="text-xs text-gray-400 italic">No matches for "{query}"</p>
        ) : (
          <JsonNode key={treeKey} value={data} depth={0} defaultOpen={allOpen} query={query} />
        )}
      </div>
    </div>
  );
};

export default CodeSnippetView;
